import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Spinner,
} from "reactstrap";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// "2026-10" moved by +/- months, still as "YYYY-MM"
const shiftMonth = (month, delta) => {
  const [year, m] = month.split("-").map(Number);
  const date = new Date(year, m - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const CarsTable = ({ cars, showTasks, onOpen }) =>
  cars.length === 0 ? (
    <p className="text-muted mb-0">{strings.nodata}</p>
  ) : (
    <Table size="sm" responsive className="mb-0">
      <thead>
        <tr>
          <th>{strings.date}</th>
          <th>{strings.report_plate}</th>
          <th>{strings.report_vehicle}</th>
          {showTasks && <th>{strings.report_tasks}</th>}
        </tr>
      </thead>
      <tbody>
        {cars.map((car) => (
          <tr
            key={car.id}
            // a row opens the car's job card, when it came in on one
            onClick={car.jobCardId ? () => onOpen(car) : undefined}
            style={car.jobCardId ? { cursor: "pointer" } : undefined}
            title={car.jobCardId ? strings.job_card : undefined}
          >
            <td>{new Date(car.at).toLocaleDateString()}</td>
            <td>{car.plate}</td>
            <td>{car.vehicle}</td>
            {showTasks && <td>{(car.tasks || []).join(", ")}</td>}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="font-weight-bold">
          <td>{strings.report_total}</td>
          <td colSpan={2}>
            {cars.length} {strings.report_cars}
          </td>
          {showTasks && (
            <td>
              {cars.reduce((sum, car) => sum + (car.tasks || []).length, 0)}{" "}
              {strings.report_tasks_done}
            </td>
          )}
        </tr>
      </tfoot>
    </Table>
  );

/**
 * A staff member's month: how many cars they received and how many they
 * fixed, with the cars behind each number. The month can be stepped back and
 * forth or picked directly.
 */
class WorkerReportModal extends React.Component {
  state = { month: currentMonth(), report: null, loading: false };
  engine = new RequestEngine();

  componentDidUpdate(prevProps) {
    const { worker } = this.props;
    if (worker && (!prevProps.worker || prevProps.worker.id !== worker.id)) {
      this.setState({ month: currentMonth(), report: null }, this.load);
    }
  }

  load = async () => {
    const { worker, merchantId } = this.props;
    const { month } = this.state;
    if (!worker || !merchantId || !month) return;
    this.setState({ loading: true });
    const response = await this.engine.getItem(
      `merchants/${merchantId}/workers/${worker.id}/report?month=${month}`
    );
    // a late answer for a month no longer shown must not overwrite it
    if (month !== this.state.month) return;
    this.setState({
      loading: false,
      report: response && response.status === 200 ? response.data : null,
    });
  };

  openJobCard = (car) => {
    const { history, onClose } = this.props;
    onClose();
    history.push({
      pathname: `/admin/merchants-branches/${car.branchId}/job-cards`,
      state: { openJobCardId: car.jobCardId },
    });
  };

  setMonth = (month) => {
    if (!month) return;
    this.setState({ month }, this.load);
  };

  render() {
    const { worker, onClose } = this.props;
    const { month, report, loading } = this.state;
    const isMechanic = worker && (worker.role || "mechanic") === "mechanic";

    const sections = [
      {
        key: "received",
        title: strings.report_cars_received,
        data: report && report.received,
      },
      {
        key: "fixed",
        title: strings.report_cars_fixed,
        data: report && report.fixed,
        showTasks: true,
      },
    ];
    // a mechanic is judged by what they fixed, so lead with it
    if (isMechanic) sections.reverse();

    return (
      <Modal isOpen={!!worker} toggle={onClose} size="lg">
        <ModalHeader toggle={onClose}>
          {strings.report_monthly}
          {worker ? ` — ${worker.name}` : ""}
        </ModalHeader>
        <ModalBody>
          <div className="d-flex align-items-center mb-3">
            <Button
              color="link"
              onClick={() => this.setMonth(shiftMonth(month, -1))}
              aria-label={strings.previous}
            >
              <i className="fa fa-chevron-left" />
            </Button>
            <Input
              type="month"
              value={month}
              max={currentMonth()}
              onChange={(e) => this.setMonth(e.target.value)}
              style={{ maxWidth: 200 }}
            />
            <Button
              color="link"
              onClick={() => this.setMonth(shiftMonth(month, 1))}
              disabled={month >= currentMonth()}
              aria-label={strings.next}
            >
              <i className="fa fa-chevron-right" />
            </Button>
            {loading && <Spinner size="sm" className="ml-2" />}
          </div>

          <Row>
            {sections.map((section) => (
              <Col md="6" key={section.key}>
                <Card className="text-center mb-3">
                  <CardBody>
                    <div className="text-muted">{section.title}</div>
                    <h2 className="mb-0">
                      {section.data ? section.data.count : "—"}
                    </h2>
                    {section.key === "fixed" && section.data && (
                      <small className="text-muted">
                        {section.data.tasks} {strings.report_tasks_done}
                      </small>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {report &&
            sections.map((section) => (
              <div key={section.key} className="mb-4">
                <h6>{section.title}</h6>
                <CarsTable
                  cars={section.data.cars}
                  showTasks={section.showTasks}
                  onOpen={this.openJobCard}
                />
              </div>
            ))}
        </ModalBody>
      </Modal>
    );
  }
}

export default WorkerReportModal;
